/**
 * Batch Configuration Form
 *
 * Form for configuring and starting batch simulations.
 * Supports prompt variations, multiple personas, and cost estimation.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Paper,
  Divider,
  LinearProgress,
  Checkbox,
  ListItemText,
  OutlinedInput,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachMoney as CostIcon,
  Science as SandboxIcon,
  Public as LiveIcon,
  Security as CBCIcon,
  Psychology as ParasocialIcon,
  SmartToy as ModelIcon,
  Replay as ReplayIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import {
  getAdvancedSimulationClientPersonas,
  getAdvancedSimulationTherapistPersonas,
  startAdvancedSimulationBatch,
  rerunAdvancedSimulationBatch,
  listAdvancedSimulationBatches,
  getAdvancedSimulationBatchStatus,
  estimateBatchCost,
  startOnboardingBatch,
  getOnboardingFlows
} from '../../../services/advancedSimulationApi';
import { listSandboxPersonas } from '../../../services/researcherApi';
import { getAvailableLLMModels, getLLMConfiguration } from '../../../services/api';
import BatchProgressViewer from './BatchProgressViewer';
import BatchResultsAnalyzer from './BatchResultsAnalyzer';

function BatchConfigForm({ token, onBatchCompleted, isResearcher = false }) {
  const navigate = useNavigate();
  const [liveClientPersonas, setLiveClientPersonas] = useState([]);
  const [liveTherapistPersonas, setLiveTherapistPersonas] = useState([]);
  const [sandboxClientPersonas, setSandboxClientPersonas] = useState([]);
  const [sandboxTherapistPersonas, setSandboxTherapistPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [costEstimate, setCostEstimate] = useState(null);
  const [recentBatches, setRecentBatches] = useState([]);
  const [showBatchHistory, setShowBatchHistory] = useState(false);

  // Persona filter for researchers
  const [personaFilter, setPersonaFilter] = useState('all'); // 'all', 'live', 'sandbox'

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedClientPersonas, setSelectedClientPersonas] = useState([]); // Array of { key, source }
  const [selectedTherapistPersonas, setSelectedTherapistPersonas] = useState([]); // Array of { key, source }
  const [runsPerCombination, setRunsPerCombination] = useState(1);
  const [maxTurnsPerRun, setMaxTurnsPerRun] = useState(50);
  const [parallelism, setParallelism] = useState(3);
  const [promptVariations, setPromptVariations] = useState([]);

  // Simulation type: therapy (default), onboarding, full_journey
  const [simulationType, setSimulationType] = useState('therapy');
  const [onboardingFlows, setOnboardingFlows] = useState([]);
  const [selectedFlowId, setSelectedFlowId] = useState('');
  const [maxTurnsPerStage, setMaxTurnsPerStage] = useState(20);
  const [maxTherapyTurns, setMaxTherapyTurns] = useState(20);

  // CBC (Conversational Boundary Control) batch options
  const [cbcEnabled, setCbcEnabled] = useState(false);
  const [cbcPresetsToTest, setCbcPresetsToTest] = useState(['moderate']);
  const [cbcIncludeDisabledControl, setCbcIncludeDisabledControl] = useState(true);
  const [cbcClassificationMethodsToTest, setCbcClassificationMethodsToTest] = useState(['keywords_only']);

  // Parasocial Detection batch options
  const [parasocialEnabled, setParasocialEnabled] = useState(false);
  const [parasocialModesToTest, setParasocialModesToTest] = useState(['moderate']);
  const [parasocialIncludeDisabledControl, setParasocialIncludeDisabledControl] = useState(true);

  // Model Configuration batch options
  const [modelTestingEnabled, setModelTestingEnabled] = useState(false);
  const [availableModels, setAvailableModels] = useState({ openai: [], gemini: [], vertex_ai: [] });
  const [platformConfig, setPlatformConfig] = useState(null);
  const [clientModelsToTest, setClientModelsToTest] = useState(['gpt-4o']);
  const [therapistModelsToTest, setTherapistModelsToTest] = useState([{ provider: 'vertex_ai', model: 'gemini-2.5-flash' }]);
  const [modelIncludePlatformDefaultControl, setModelIncludePlatformDefaultControl] = useState(true);

  // Load personas and flows on mount
  useEffect(() => {
    loadPersonas();
    loadOnboardingFlows();
  }, [token, isResearcher]);

  const loadOnboardingFlows = async () => {
    try {
      const res = await getOnboardingFlows(token);
      setOnboardingFlows(res.flows || []);
    } catch (err) {
      console.error('Failed to load onboarding flows:', err);
    }
  };

  // Track if initial load has happened
  const [modelsInitialized, setModelsInitialized] = useState(false);

  // Load available models and platform config
  useEffect(() => {
    loadModelsAndConfig();
  }, [token]);

  const loadModelsAndConfig = async () => {
    try {
      const [modelsRes, configRes] = await Promise.all([
        getAvailableLLMModels(token, { activeOnly: true }),
        getLLMConfiguration(token)
      ]);

      // Group models by provider
      const grouped = { openai: [], gemini: [], vertex_ai: [] };
      (modelsRes.models || []).forEach(model => {
        if (grouped[model.provider]) {
          grouped[model.provider].push(model);
        }
      });
      setAvailableModels(grouped);
      setPlatformConfig(configRes.configuration);

      // Only set defaults on initial load, not when re-fetching
      if (!modelsInitialized && configRes.configuration) {
        const config = configRes.configuration;
        setClientModelsToTest([config.simulatorClientModel || 'gpt-4o']);
        setTherapistModelsToTest([{
          provider: config.primaryProvider || 'vertex_ai',
          model: config.primaryModel || 'gemini-2.5-flash'
        }]);
        setModelsInitialized(true);
      }
    } catch (err) {
      console.error('Failed to load LLM configuration:', err);
    }
  };

  // Update cost estimate when config changes
  useEffect(() => {
    updateCostEstimate();
  }, [selectedClientPersonas, selectedTherapistPersonas, runsPerCombination, maxTurnsPerRun, promptVariations, cbcEnabled, cbcPresetsToTest, cbcIncludeDisabledControl, cbcClassificationMethodsToTest, parasocialEnabled, parasocialModesToTest, parasocialIncludeDisabledControl]);

  const loadPersonas = async () => {
    setLoading(true);
    try {
      // Load live personas
      const [clientRes, therapistRes] = await Promise.all([
        getAdvancedSimulationClientPersonas(token),
        getAdvancedSimulationTherapistPersonas(token)
      ]);
      setLiveClientPersonas(clientRes.personas || []);
      setLiveTherapistPersonas(therapistRes.personas || []);

      // Load sandbox personas if researcher
      if (isResearcher) {
        try {
          const sandboxRes = await listSandboxPersonas();
          const clientSandbox = (sandboxRes.owned || []).concat(sandboxRes.shared || [])
            .filter(p => p.personaType === 'client');
          const therapistSandbox = (sandboxRes.owned || []).concat(sandboxRes.shared || [])
            .filter(p => p.personaType === 'therapist');
          setSandboxClientPersonas(clientSandbox);
          setSandboxTherapistPersonas(therapistSandbox);
        } catch (sandboxErr) {
          console.error('Failed to load sandbox personas:', sandboxErr);
          // Don't fail completely if sandbox load fails
        }
      }
    } catch (err) {
      setError('Failed to load personas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Compute filtered personas based on filter selection
  const getFilteredClientPersonas = () => {
    const live = liveClientPersonas.map(p => ({ ...p, source: 'live' }));
    const sandbox = sandboxClientPersonas.map(p => ({ ...p, source: 'sandbox' }));

    if (!isResearcher || personaFilter === 'live') return live;
    if (personaFilter === 'sandbox') return sandbox;
    return [...sandbox, ...live]; // 'all' - sandbox first
  };

  const getFilteredTherapistPersonas = () => {
    const live = liveTherapistPersonas.map(p => ({ ...p, source: 'live' }));
    const sandbox = sandboxTherapistPersonas.map(p => ({ ...p, source: 'sandbox' }));

    if (!isResearcher || personaFilter === 'live') return live;
    if (personaFilter === 'sandbox') return sandbox;
    return [...sandbox, ...live]; // 'all' - sandbox first
  };

  const clientPersonas = getFilteredClientPersonas();
  const therapistPersonas = getFilteredTherapistPersonas();

  // Handle client persona selection with source tracking
  const handleClientPersonaChange = (selectedKeys) => {
    const selected = selectedKeys.map(key => {
      const persona = clientPersonas.find(p => p.key === key);
      return { key, source: persona?.source || 'live' };
    });
    setSelectedClientPersonas(selected);
  };

  // Handle therapist persona selection with source tracking
  const handleTherapistPersonaChange = (selectedKeys) => {
    const selected = selectedKeys.map(key => {
      const persona = therapistPersonas.find(p => p.key === key);
      return { key, source: persona?.source || 'live' };
    });
    setSelectedTherapistPersonas(selected);
  };

  // Get selected keys for Select component values
  const getSelectedClientKeys = () => selectedClientPersonas.map(p => p.key);
  const getSelectedTherapistKeys = () => selectedTherapistPersonas.map(p => p.key);

  const updateCostEstimate = async () => {
    if (selectedClientPersonas.length === 0 || selectedTherapistPersonas.length === 0) {
      setCostEstimate(null);
      return;
    }

    try {
      const estimate = await estimateBatchCost({
        clientPersonaKeys: selectedClientPersonas.map(p => p.key),
        therapistPersonaKeys: selectedTherapistPersonas.map(p => p.key),
        promptVariations,
        runsPerCombination: parseInt(runsPerCombination),
        maxTurnsPerRun: parseInt(maxTurnsPerRun)
      }, token);
      setCostEstimate(estimate);
    } catch (err) {
      console.error('Error estimating cost:', err);
    }
  };

  const handleAddVariation = () => {
    setPromptVariations([
      ...promptVariations,
      { name: `Variation ${promptVariations.length + 1}`, personaKey: '', promptOverride: '' }
    ]);
  };

  const handleRemoveVariation = (index) => {
    setPromptVariations(promptVariations.filter((_, i) => i !== index));
  };

  const handleUpdateVariation = (index, field, value) => {
    const updated = [...promptVariations];
    updated[index][field] = value;
    setPromptVariations(updated);
  };

  const handleStartBatch = async () => {
    if (!name.trim()) {
      setError('Please enter a batch name');
      return;
    }

    const isOnboarding = simulationType === 'onboarding' || simulationType === 'full_journey';

    if (selectedClientPersonas.length === 0) {
      setError('Please select at least one client persona');
      return;
    }
    if (!isOnboarding && selectedTherapistPersonas.length === 0) {
      setError('Please select at least one therapist persona');
      return;
    }
    if (isOnboarding && !selectedFlowId) {
      setError('Please select an onboarding flow');
      return;
    }

    setRunning(true);
    setError(null);

    try {
      let result;

      if (isOnboarding) {
        // Start onboarding/full_journey batch
        const selectedFlow = onboardingFlows.find(f => f._id === selectedFlowId);
        result = await startOnboardingBatch({
          name: name.trim(),
          description: description.trim(),
          simulationType,
          clientPersonaKeys: selectedClientPersonas.map(p => p.key),
          clientPersonaSelections: selectedClientPersonas,
          runsPerCombination: parseInt(runsPerCombination),
          maxTurnsPerRun: parseInt(maxTurnsPerRun),
          parallelism: parseInt(parallelism),
          flowId: selectedFlowId,
          flowSlug: selectedFlow?.slug,
          maxTurnsPerStage: parseInt(maxTurnsPerStage),
          maxTherapyTurns: parseInt(maxTherapyTurns)
        }, token);
      } else {
        // Start standard therapy batch
        const batchConfig = {
          name: name.trim(),
          description: description.trim(),
          clientPersonaKeys: selectedClientPersonas.map(p => p.key),
          therapistPersonaKeys: selectedTherapistPersonas.map(p => p.key),
          clientPersonaSelections: selectedClientPersonas,
          therapistPersonaSelections: selectedTherapistPersonas,
          promptVariations: promptVariations.filter(v => v.name && v.personaKey && v.promptOverride),
          runsPerCombination: parseInt(runsPerCombination),
          maxTurnsPerRun: parseInt(maxTurnsPerRun),
          parallelism: parseInt(parallelism)
        };

        // Add CBC config if enabled
        if (cbcEnabled) {
          batchConfig.cbcConfig = {
            enabled: true,
            presetsToTest: cbcPresetsToTest,
            includeDisabledAsControl: cbcIncludeDisabledControl,
            classificationMethodsToTest: cbcClassificationMethodsToTest
          };
        }

        // Add parasocial config if enabled
        if (parasocialEnabled) {
          batchConfig.parasocialConfig = {
            enabled: true,
            disclosureModesToTest: parasocialModesToTest,
            includeDisabledAsControl: parasocialIncludeDisabledControl
          };
        }

        // Add model configuration if enabled
        if (modelTestingEnabled) {
          batchConfig.modelConfig = {
            enabled: true,
            clientModelsToTest: clientModelsToTest,
            therapistModelsToTest: therapistModelsToTest,
            includePlatformDefaultAsControl: modelIncludePlatformDefaultControl
          };
        }

        result = await startAdvancedSimulationBatch(batchConfig, token);
      }

      // Fetch full batch status
      const batch = await getAdvancedSimulationBatchStatus(result._id, token);
      setCurrentBatch(batch);
      // Deep-link to the batch progress route so refresh / back button work,
      // and so the user can return to the live progress view from anywhere.
      if (batch?._id) {
        navigate(`/admin/dashboard/advanced-simulation/batch/${batch._id}`);
      }
    } catch (err) {
      setError(err.error || 'Failed to start batch');
      setRunning(false);
    }
  };

  const handleBatchComplete = (batch) => {
    setRunning(false);
    setCurrentBatch(batch);
    onBatchCompleted?.(batch.batchId);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // If batch is running or completed, show progress/results
  if (currentBatch) {
    if (currentBatch.status === 'running') {
      return (
        <BatchProgressViewer
          batchId={currentBatch._id}
          token={token}
          onComplete={handleBatchComplete}
        />
      );
    } else if (['completed', 'partial_failure'].includes(currentBatch.status)) {
      return (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setCurrentBatch(null)}
            >
              New Batch
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                try {
                  const result = await rerunAdvancedSimulationBatch(currentBatch._id, token);
                  setCurrentBatch({ _id: result._id, status: 'running' });
                } catch (err) {
                  console.error('Re-run failed:', err);
                  alert('Failed to re-run batch: ' + (err.error || err.message || 'Unknown error'));
                }
              }}
            >
              Re-run Batch
            </Button>
          </Box>
          <BatchResultsAnalyzer
            batchId={currentBatch._id}
            token={token}
          />
        </Box>
      );
    }
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configure Batch Simulation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Run multiple simulations in parallel with different persona combinations and prompt variations.
      </Typography>

      <Button
        variant="text"
        size="small"
        startIcon={<HistoryIcon />}
        onClick={async () => {
          if (!showBatchHistory) {
            try {
              const result = await listAdvancedSimulationBatches({ limit: 15 }, token);
              setRecentBatches(result.batches || []);
            } catch (err) { console.error('Error loading batches:', err); }
          }
          setShowBatchHistory(!showBatchHistory);
        }}
        sx={{ mb: 1 }}
      >
        {showBatchHistory ? 'Hide' : 'Show'} Recent Batches
      </Button>

      {showBatchHistory && recentBatches.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 3, p: 2, maxHeight: 300, overflowY: 'auto' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Recent Batches</Typography>
          {recentBatches.map((batch) => (
            <Box
              key={batch._id}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                py: 0.75, px: 1, borderBottom: '1px solid #eee',
                '&:last-child': { borderBottom: 'none' }
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {batch.name || 'Unnamed'}
                  <Chip
                    label={batch.status}
                    size="small"
                    color={batch.status === 'completed' ? 'success' : batch.status === 'running' ? 'warning' : 'default'}
                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                  />
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {batch._id.toString().slice(-6)} | {new Date(batch.createdAt).toLocaleDateString()} {new Date(batch.createdAt).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}
                  {batch.progress?.totalRuns && ` | ${batch.progress.totalRuns} runs`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {['completed', 'partial_failure'].includes(batch.status) && (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setCurrentBatch({ _id: batch._id, status: batch.status })}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<ReplayIcon />}
                      onClick={async () => {
                        try {
                          const result = await rerunAdvancedSimulationBatch(batch._id, token);
                          setCurrentBatch({ _id: result._id, status: 'running' });
                          setShowBatchHistory(false);
                        } catch (err) {
                          alert('Re-run failed: ' + (err.error || err.message));
                        }
                      }}
                    >
                      Re-run
                    </Button>
                  </>
                )}
                {batch.status === 'running' && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setCurrentBatch({ _id: batch._id, status: 'running' })}
                  >
                    View Progress
                  </Button>
                )}
              </Box>
            </Box>
          ))}
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Persona Filter for Researchers */}
      {isResearcher && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Persona Source Filter
          </Typography>
          <ToggleButtonGroup
            value={personaFilter}
            exclusive
            onChange={(e, value) => value && setPersonaFilter(value)}
            size="small"
          >
            <ToggleButton value="all">
              All Personas
            </ToggleButton>
            <ToggleButton value="sandbox">
              <SandboxIcon sx={{ mr: 0.5 }} fontSize="small" />
              Sandbox Only
            </ToggleButton>
            <ToggleButton value="live">
              <LiveIcon sx={{ mr: 0.5 }} fontSize="small" />
              Live Only
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Sandbox: {sandboxClientPersonas.length} clients, {sandboxTherapistPersonas.length} therapists
          </Typography>
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Basic Info */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Batch Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={running}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={running}
          />
        </Grid>

        {/* Simulation Type Selector */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>Simulation Type</Typography>
          <ToggleButtonGroup
            value={simulationType}
            exclusive
            onChange={(e, value) => value && setSimulationType(value)}
            size="small"
            disabled={running}
          >
            <ToggleButton value="therapy">Therapy Only</ToggleButton>
            <ToggleButton value="onboarding">Onboarding + Triage</ToggleButton>
            <ToggleButton value="full_journey">Full Journey</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {simulationType === 'therapy' && 'Standard therapy conversation simulation'}
            {simulationType === 'onboarding' && 'Multi-stage onboarding through to triage routing (stops after specialist assigned)'}
            {simulationType === 'full_journey' && 'Complete onboarding + triage + continues into therapy conversation'}
          </Typography>
        </Grid>

        {/* Onboarding Flow Selector (onboarding/full_journey only) */}
        {(simulationType === 'onboarding' || simulationType === 'full_journey') && (
          <>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Onboarding Flow</InputLabel>
                <Select
                  value={selectedFlowId}
                  onChange={(e) => setSelectedFlowId(e.target.value)}
                  label="Onboarding Flow"
                  disabled={running}
                >
                  {onboardingFlows.map((flow) => (
                    <MenuItem key={flow._id} value={flow._id}>
                      {flow.name} ({flow.stageCount} stages)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedFlowId && (
                <Box sx={{ mt: 1 }}>
                  {onboardingFlows.find(f => f._id === selectedFlowId)?.stages?.map((s, i) => (
                    <Chip
                      key={i}
                      label={`${s.stageNumber}. ${s.stageName}`}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Max Turns/Stage"
                value={maxTurnsPerStage}
                onChange={(e) => setMaxTurnsPerStage(e.target.value)}
                inputProps={{ min: 5, max: 50 }}
                disabled={running}
                helperText="Safety limit per stage"
              />
            </Grid>
            {simulationType === 'full_journey' && (
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Therapy Turns"
                  value={maxTherapyTurns}
                  onChange={(e) => setMaxTherapyTurns(e.target.value)}
                  inputProps={{ min: 5, max: 50 }}
                  disabled={running}
                  helperText="Turns after triage"
                />
              </Grid>
            )}
          </>
        )}

        {/* Persona Selection */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Client Personas</InputLabel>
            <Select
              multiple
              value={getSelectedClientKeys()}
              onChange={(e) => handleClientPersonaChange(e.target.value)}
              input={<OutlinedInput label="Client Personas" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((key) => {
                    const persona = selectedClientPersonas.find(p => p.key === key);
                    const isSandbox = persona?.source === 'sandbox';
                    return (
                      <Chip
                        key={key}
                        label={key}
                        size="small"
                        color={isSandbox ? 'secondary' : 'default'}
                      />
                    );
                  })}
                </Box>
              )}
              disabled={running}
            >
              {clientPersonas.map((persona) => (
                <MenuItem key={`${persona.source}-${persona.key}`} value={persona.key}>
                  <Checkbox checked={getSelectedClientKeys().includes(persona.key)} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {persona.source === 'sandbox' && (
                      <Chip label="Sandbox" size="small" color="secondary" sx={{ height: 20 }} />
                    )}
                    <ListItemText primary={persona.name} secondary={persona.key} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Therapist Persona Selector - hidden for onboarding (therapists come from flow stages) */}
        {simulationType === 'therapy' && (
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Therapist Personas</InputLabel>
            <Select
              multiple
              value={getSelectedTherapistKeys()}
              onChange={(e) => handleTherapistPersonaChange(e.target.value)}
              input={<OutlinedInput label="Therapist Personas" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((key) => {
                    const persona = selectedTherapistPersonas.find(p => p.key === key);
                    const isSandbox = persona?.source === 'sandbox';
                    return (
                      <Chip
                        key={key}
                        label={key}
                        size="small"
                        color={isSandbox ? 'secondary' : 'default'}
                      />
                    );
                  })}
                </Box>
              )}
              disabled={running}
            >
              {therapistPersonas.map((persona) => (
                <MenuItem key={`${persona.source}-${persona.key}`} value={persona.key}>
                  <Checkbox checked={getSelectedTherapistKeys().includes(persona.key)} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {persona.source === 'sandbox' && (
                      <Chip label="Sandbox" size="small" color="secondary" sx={{ height: 20 }} />
                    )}
                    <ListItemText primary={persona.name} secondary={persona.key} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        )}

        {/* Run Configuration */}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            type="number"
            label="Runs per Combination"
            value={runsPerCombination}
            onChange={(e) => setRunsPerCombination(e.target.value)}
            inputProps={{ min: 1, max: 20 }}
            helperText="How many times to run each config"
            disabled={running}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            type="number"
            label="Max Turns per Run"
            value={maxTurnsPerRun}
            onChange={(e) => setMaxTurnsPerRun(e.target.value)}
            inputProps={{ min: 10, max: 200 }}
            disabled={running}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            type="number"
            label="Parallelism"
            value={parallelism}
            onChange={(e) => setParallelism(e.target.value)}
            inputProps={{ min: 1, max: 10 }}
            helperText="Concurrent simulations"
            disabled={running}
          />
        </Grid>

        {/* Prompt Variations */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                Prompt Variations (Optional)
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddVariation}
                disabled={running}
                size="small"
              >
                Add Variation
              </Button>
            </Box>

            {promptVariations.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No variations defined. All runs will use default prompts.
              </Typography>
            ) : (
              promptVariations.map((variation, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Variation Name"
                        value={variation.name}
                        onChange={(e) => handleUpdateVariation(index, 'name', e.target.value)}
                        disabled={running}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Apply to Persona</InputLabel>
                        <Select
                          value={variation.personaKey}
                          onChange={(e) => handleUpdateVariation(index, 'personaKey', e.target.value)}
                          label="Apply to Persona"
                          disabled={running}
                        >
                          {[...selectedClientPersonas, ...selectedTherapistPersonas].map((key) => (
                            <MenuItem key={key} value={key}>{key}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Prompt Override"
                        value={variation.promptOverride}
                        onChange={(e) => handleUpdateVariation(index, 'promptOverride', e.target.value)}
                        multiline
                        rows={2}
                        disabled={running}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton
                        onClick={() => handleRemoveVariation(index)}
                        disabled={running}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        {/* CBC Configuration Section */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CBCIcon color={cbcEnabled ? 'primary' : 'disabled'} />
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Conversational Boundary Control (CBC) Testing
              </Typography>
              <Chip
                label={cbcEnabled ? 'Enabled' : 'Disabled'}
                size="small"
                color={cbcEnabled ? 'primary' : 'default'}
                onClick={() => setCbcEnabled(!cbcEnabled)}
                sx={{ cursor: 'pointer', ml: 'auto' }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Test different CBC presets to compare their effectiveness at maintaining therapeutic boundaries.
            </Typography>

            {cbcEnabled && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Select Presets to Compare:</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {['conservative', 'moderate', 'permissive'].map(preset => (
                      <Chip
                        key={preset}
                        label={preset.charAt(0).toUpperCase() + preset.slice(1)}
                        color={cbcPresetsToTest.includes(preset) ? 'primary' : 'default'}
                        variant={cbcPresetsToTest.includes(preset) ? 'filled' : 'outlined'}
                        onClick={() => {
                          if (cbcPresetsToTest.includes(preset)) {
                            setCbcPresetsToTest(cbcPresetsToTest.filter(p => p !== preset));
                          } else {
                            setCbcPresetsToTest([...cbcPresetsToTest, preset]);
                          }
                        }}
                        disabled={running}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Select Classification Methods to Compare:</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {[
                      { key: 'inline_prompt', label: 'Inline Prompt (Recommended)' },
                      { key: 'keywords_only', label: 'Keywords Only' }
                    ].map(method => (
                      <Chip
                        key={method.key}
                        label={method.label}
                        color={cbcClassificationMethodsToTest.includes(method.key) ? 'primary' : 'default'}
                        variant={cbcClassificationMethodsToTest.includes(method.key) ? 'filled' : 'outlined'}
                        onClick={() => {
                          if (cbcClassificationMethodsToTest.includes(method.key)) {
                            setCbcClassificationMethodsToTest(cbcClassificationMethodsToTest.filter(m => m !== method.key));
                          } else {
                            setCbcClassificationMethodsToTest([...cbcClassificationMethodsToTest, method.key]);
                          }
                        }}
                        disabled={running}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Keywords: Fast but may have false positives | Separate LLM: Higher accuracy, +100-300ms | Inline: Zero latency, medium accuracy
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      checked={cbcIncludeDisabledControl}
                      onChange={(e) => setCbcIncludeDisabledControl(e.target.checked)}
                      disabled={running}
                    />
                    <Typography variant="body2">
                      Include "Disabled" as control group (recommended for comparison)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    CBC will run <strong>{(cbcPresetsToTest.length + (cbcIncludeDisabledControl ? 1 : 0)) * cbcClassificationMethodsToTest.length}</strong> configuration(s) per persona combination
                    ({cbcPresetsToTest.length + (cbcIncludeDisabledControl ? 1 : 0)} presets × {cbcClassificationMethodsToTest.length} classification methods).
                  </Alert>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>

        {/* Parasocial Detection Configuration Section */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ParasocialIcon color={parasocialEnabled ? 'secondary' : 'disabled'} />
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Parasocial Detection Testing
              </Typography>
              <Chip
                label={parasocialEnabled ? 'Enabled' : 'Disabled'}
                size="small"
                color={parasocialEnabled ? 'secondary' : 'default'}
                onClick={() => setParasocialEnabled(!parasocialEnabled)}
                sx={{ cursor: 'pointer', ml: 'auto' }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Test different disclosure modes to compare their effectiveness at detecting and addressing unhealthy attachment patterns.
            </Typography>

            {parasocialEnabled && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Select Disclosure Modes to Compare:</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {['conservative', 'moderate', 'proactive'].map(mode => (
                      <Chip
                        key={mode}
                        label={mode.charAt(0).toUpperCase() + mode.slice(1)}
                        color={parasocialModesToTest.includes(mode) ? 'secondary' : 'default'}
                        variant={parasocialModesToTest.includes(mode) ? 'filled' : 'outlined'}
                        onClick={() => {
                          if (parasocialModesToTest.includes(mode)) {
                            setParasocialModesToTest(parasocialModesToTest.filter(m => m !== mode));
                          } else {
                            setParasocialModesToTest([...parasocialModesToTest, mode]);
                          }
                        }}
                        disabled={running}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      checked={parasocialIncludeDisabledControl}
                      onChange={(e) => setParasocialIncludeDisabledControl(e.target.checked)}
                      disabled={running}
                    />
                    <Typography variant="body2">
                      Include "Disabled" as control group (recommended for comparison)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Parasocial will run <strong>{parasocialModesToTest.length + (parasocialIncludeDisabledControl ? 1 : 0)}</strong> configuration(s) per persona combination,
                    multiplying your total runs by <strong>{parasocialModesToTest.length + (parasocialIncludeDisabledControl ? 1 : 0)}x</strong>.
                  </Alert>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>

        {/* Model Configuration Section */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ModelIcon color={modelTestingEnabled ? 'info' : 'disabled'} />
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Model Comparison Testing
              </Typography>
              <Chip
                label={modelTestingEnabled ? 'Enabled' : 'Disabled'}
                size="small"
                color={modelTestingEnabled ? 'info' : 'default'}
                onClick={() => setModelTestingEnabled(!modelTestingEnabled)}
                sx={{ cursor: 'pointer', ml: 'auto' }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Compare different LLM models to evaluate performance, quality, and cost differences.
            </Typography>

            {modelTestingEnabled && (
              <Grid container spacing={2}>
                {/* Client Models (OpenAI) */}
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Client Models to Compare (OpenAI):</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(availableModels.openai.length > 0 ? availableModels.openai : [
                      { id: 'gpt-4o', displayName: 'GPT-4o' },
                      { id: 'gpt-4o-mini', displayName: 'GPT-4o Mini' },
                      { id: 'gpt-4', displayName: 'GPT-4' },
                      { id: 'gpt-3.5-turbo', displayName: 'GPT-3.5 Turbo' }
                    ]).map(model => (
                      <Chip
                        key={model.id}
                        label={model.displayName || model.id}
                        color={clientModelsToTest.includes(model.id) ? 'info' : 'default'}
                        variant={clientModelsToTest.includes(model.id) ? 'filled' : 'outlined'}
                        onClick={() => {
                          if (clientModelsToTest.includes(model.id)) {
                            setClientModelsToTest(clientModelsToTest.filter(m => m !== model.id));
                          } else {
                            setClientModelsToTest([...clientModelsToTest, model.id]);
                          }
                        }}
                        disabled={running}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Grid>

                {/* Therapist Models (Gemini) */}
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Therapist Models to Compare (Google Gemini):</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {['vertex_ai', 'gemini'].map(provider => (
                      (availableModels[provider]?.length > 0 ? availableModels[provider] : [
                        { id: 'gemini-2.5-flash', displayName: 'Gemini 2.5 Flash', provider },
                        { id: 'gemini-2.0-flash', displayName: 'Gemini 2.0 Flash', provider },
                        { id: 'gemini-1.5-pro', displayName: 'Gemini 1.5 Pro', provider },
                        { id: 'gemini-1.5-flash', displayName: 'Gemini 1.5 Flash', provider }
                      ].filter(m => m.provider === provider)).map(model => {
                        const isSelected = therapistModelsToTest.some(
                          m => m.provider === provider && m.model === model.id
                        );
                        return (
                          <Chip
                            key={`${provider}-${model.id}`}
                            label={`${model.displayName || model.id} (${provider === 'vertex_ai' ? 'Vertex' : 'API'})`}
                            color={isSelected ? 'info' : 'default'}
                            variant={isSelected ? 'filled' : 'outlined'}
                            onClick={() => {
                              if (isSelected) {
                                setTherapistModelsToTest(
                                  therapistModelsToTest.filter(m => !(m.provider === provider && m.model === model.id))
                                );
                              } else {
                                setTherapistModelsToTest([
                                  ...therapistModelsToTest,
                                  { provider, model: model.id }
                                ]);
                              }
                            }}
                            disabled={running}
                            sx={{ cursor: 'pointer' }}
                          />
                        );
                      })
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      checked={modelIncludePlatformDefaultControl}
                      onChange={(e) => setModelIncludePlatformDefaultControl(e.target.checked)}
                      disabled={running}
                    />
                    <Typography variant="body2">
                      Include platform default as control group
                      {platformConfig && (
                        <Typography component="span" variant="caption" color="text.secondary">
                          {' '}(Client: {platformConfig.simulatorClientModel || 'gpt-4o'},
                          Therapist: {platformConfig.primaryModel || 'gemini-2.5-flash'})
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Model testing will run <strong>{clientModelsToTest.length * therapistModelsToTest.length + (modelIncludePlatformDefaultControl ? 1 : 0)}</strong> model
                    combination(s) per persona combination ({clientModelsToTest.length} client × {therapistModelsToTest.length} therapist
                    {modelIncludePlatformDefaultControl ? ' + 1 control' : ''}).
                  </Alert>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>

        {/* Cost Estimate */}
        {costEstimate && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CostIcon color="primary" />
                  <Typography variant="subtitle1">Cost Estimate</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Total Runs</Typography>
                    <Typography variant="h6">{costEstimate.totalRuns}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Est. Cost/Run</Typography>
                    <Typography variant="h6">${costEstimate.estimatedCostPerRun?.toFixed(4)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Total Est. Cost</Typography>
                    <Typography variant="h6" color="primary">${costEstimate.totalEstimatedCost?.toFixed(4)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Breakdown</Typography>
                    <Typography variant="body2">
                      {costEstimate.breakdown?.combinations} combos × {costEstimate.breakdown?.variationMultiplier} vars × {costEstimate.breakdown?.runsPerCombination} runs
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Start Button */}
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            startIcon={running ? <CircularProgress size={20} color="inherit" /> : <RunIcon />}
            onClick={handleStartBatch}
            disabled={running || !name.trim() || selectedClientPersonas.length === 0 || (simulationType === 'therapy' && selectedTherapistPersonas.length === 0) || ((simulationType === 'onboarding' || simulationType === 'full_journey') && !selectedFlowId)}
            size="large"
          >
            {running ? 'Starting...' : `Start Batch (${costEstimate?.totalRuns || 0} runs)`}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default BatchConfigForm;
